/**
 * Keep Rebuy widgets off the single-unit variant.
 *
 * Singles are sold through Build a Bundle only. snippets/ps-buyable-variants
 * enforces that on theme-rendered surfaces; this file does the same for the
 * Rebuy widgets, which build their own markup from their own product data.
 *
 * Rebuy dispatches `rebuy.beforeProductsChange` on document with
 * { products, widget }, and hands the products over by reference — so dropping
 * the single from product.variants there decides everything downstream at once.
 * Rebuy resolves "first variant" against this same array, which is why one
 * filter is enough: the card prices the pack rather than the single, its image
 * is the pack shot, the dropdown holds only offered sizes, and Add puts the
 * right variant in the cart.
 *
 * The event fires with nothing enabled in the Rebuy admin, so the rule lives
 * here, in the repository, rather than in an app setting nobody would find
 * later. (The widget's Advanced > Callbacks editor is a second way in, and the
 * reason not to use it.) Event reference:
 * https://developers.rebuyengine.com/reference/widget-event-listeners
 *
 * Loaded from the head so the listener is attached early. Deferred is still in
 * time: Rebuy only dispatches once its own request for recommendations comes
 * back, which is far behind the parser. Verified across the product page, the
 * cart drawer opened from a collection, and repeat loads with Rebuy cached.
 *
 * One widget is exempt: the bundle builder. Build a Bundle is the place where
 * singles ARE the product — filtering them there priced every tile at the pack
 * ($35.99 instead of $6.49) and would have had shoppers building a bundle out
 * of six-packs. It identifies itself as data.config.type === 'bundle_builder'.
 *
 * A product whose every variant is a single is left untouched — filtering it
 * away would leave nothing to sell. Same fallback as ps-buyable-variants.
 *
 * Feature-detected: if Rebuy renames the event or reshapes its payload, nothing
 * here matches and the widgets keep working exactly as they do today.
 *
 * @author Maksym Mariukhnik
 */

(function () {
  'use strict';

  // Shared with the "Single" option value in the Shopify catalogue.
  var HIDDEN_VALUE = 'Single';

  function isHidden(title) {
    return (title || '').split(' / ').indexOf(HIDDEN_VALUE) !== -1;
  }

  // Build a Bundle sells the single by design — leave that widget alone.
  function sellsSingles(widget) {
    var config = widget && widget.data && widget.data.config;
    return !!config && config.type === 'bundle_builder';
  }

  function filterProducts(products) {
    if (!Array.isArray(products)) return;
    products.forEach(function (product) {
      if (!product || !Array.isArray(product.variants)) return;

      var offered = product.variants.filter(function (variant) {
        return !isHidden(variant.title);
      });
      // Nothing to drop, or nothing would be left if we did.
      if (!offered.length || offered.length === product.variants.length) return;

      product.variants = offered;

      // Rebuy resolves the offered variant off this array, so an unset selection
      // lands on a pack by itself — leave it alone and let Rebuy pick, including
      // its own preference for one that is in stock.
      if (!product.selected_variant_id) return;

      // A selection that survived from before the filter can still point at the
      // single: a cached payload, or a widget that resolved early. Left that way
      // the card prices the single, the <select> matches no option and renders
      // blank, and Add puts the single in the cart. Re-point both.
      var stillOffered = offered.some(function (variant) {
        return variant.id === product.selected_variant_id;
      });
      if (stillOffered) return;

      var replacement = offered.filter(function (variant) {
        return variant.available !== false;
      })[0] || offered[0];

      product.selected_variant_id = replacement.id;
      if (product.selected_variant) product.selected_variant = replacement;
    });
  }

  document.addEventListener('rebuy.beforeProductsChange', function (event) {
    if (sellsSingles(event.detail && event.detail.widget)) return;
    filterProducts(event.detail && event.detail.products);
  });

  // Some widgets carry their products on the instance instead of the event.
  document.addEventListener('rebuy.beforeReady', function (event) {
    var widget = event.detail && event.detail.widget;
    if (sellsSingles(widget)) return;
    filterProducts(widget && widget.products);
  });
})();
